/**
 * LoginPage — a focused "sign in" screen. For now there is exactly one option:
 * Continue with Google. Already-signed-in students are sent straight home.
 */

import { Navigate, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { GoogleSignInButton } from '@/components/GoogleSignInButton';
import { useCustomerAuth } from '@/context/CustomerAuthContext';
import { ROUTES } from '@/constants/routes';

export function LoginPage() {
  const navigate = useNavigate();
  const { status } = useCustomerAuth();

  if (status === 'authenticated') return <Navigate to={ROUTES.home} replace />;

  return (
    <main className="grid min-h-dvh place-items-center bg-canvas px-4">
      <div className="w-full max-w-sm">
        <Link
          to={ROUTES.home}
          className="mb-6 inline-flex items-center gap-1 text-sm font-medium text-muted hover:text-ink"
        >
          <ArrowLeft size={16} /> Back to store
        </Link>

        {/*
          p-6 on phones, p-8 from `sm` up. The generous padding was eating the
          width the Google button needs: at p-8 a 320px screen left only 224px
          of content, less than Google's 200px minimum plus breathing room.
        */}
        <div className="rounded-2xl border border-line bg-surface p-6 text-center shadow-card sm:p-8">
          <img
            src="/brand/rocket07-mark.png"
            alt="Rocket07"
            width={64}
            height={64}
            className="mx-auto mb-4 h-16 w-16 object-contain"
          />
          <h1 className="text-xl font-extrabold tracking-tight text-ink">
            Sign in to Rocket<span className="text-primary-600">07</span>
          </h1>
          <p className="mt-1.5 text-sm text-muted">
            Sign in to save your details, addresses and orders.
          </p>

          <div className="mt-6 flex w-full justify-center">
            {/*
              A brand new student goes straight to their details rather than
              the store: it is the one moment they are certain to fill the form
              in, and a delivery needs a mobile number. Returning students are
              not asked again.
            */}
            <GoogleSignInButton
              onSignedIn={({ isNew } = {}) =>
                navigate(isNew ? ROUTES.profileDetails : ROUTES.home, { replace: true })
              }
            />
          </div>

          <p className="mt-6 text-xs text-muted">
            By continuing you agree to Rocket07’s terms. We only use your Google account to sign you
            in.
          </p>
        </div>
      </div>
    </main>
  );
}

export default LoginPage;
